import React from 'react';
import { TrendIndicator } from '../../types/analytics';

interface MetricCardProps {
  title: string;
  value: React.ReactNode;
  subtitle?: React.ReactNode;
  trend?: TrendIndicator;
  icon?: React.ReactNode;
  color?: string;
  loading?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  subtitle,
  trend,
  icon,
  color = 'bg-white',
  loading = false,
}) => {
  const getTrendIcon = () => {
    if (!trend) return null;

    switch (trend) {
      case 'improving':
        return (
          <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'declining':
        return (
          <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        );
      case 'stable':
        return (
          <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
              clipRule="evenodd"
            />
          </svg>
        );
    }
  };

  if (loading) {
    return (
      <div className={`${color} rounded-lg shadow-sm p-6 animate-pulse`}>
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-3"></div>
        <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-1/3"></div>
      </div>
    );
  }

  return (
    <div className={`${color} rounded-lg shadow-sm p-6 transition-all hover:shadow-md`}>
      <div className="flex items-start justify-between mb-3">
        <h3 className="font-['Open_Sans'] text-sm text-[#767472] uppercase">{title}</h3>
        {icon && <div className="text-[#c6a664]">{icon}</div>}
      </div>

      <div className="flex items-end justify-between">
        <div>
          <div className="font-['Ovo'] text-3xl text-[#1f1f1f] mb-1">{value}</div>
          {subtitle && <p className="font-['Open_Sans'] text-sm text-[#767472]">{subtitle}</p>}
        </div>
        {trend && <div className="flex items-center ml-2">{getTrendIcon()}</div>}
      </div>
    </div>
  );
};

export default MetricCard;
